import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Users, Search, Eye, Check, X, Loader2, FileText, ChevronDown, ChevronUp, Link2, MapPin } from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const TIER_METAL_SM = {
  Silver:   { background: "linear-gradient(145deg, #F4F7FA 0%, #B6C2D0 55%, #6F7A8A 100%)", color: "#1A2530", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)" },
  Gold:     { background: "linear-gradient(145deg, #FBE38B 0%, #D4A93A 55%, #8C6618 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)" },
  Platinum: { background: "linear-gradient(145deg, #2B50B0 0%, #173075 55%, #0A1736 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)" },
};

const adminDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const AdminField = ({ label, value, bold, status, className = "" }) => {
  if (!value) return null;
  let cls = "font-medium text-gray-700";
  if (bold) cls = "font-bold text-[#008A99]";
  if (status) {
    const s = String(status).toUpperCase();
    cls = s === "ACTIVE" ? "font-bold text-green-600" :
      (s === "EXPIRED" || s === "TERMINATED") ? "font-bold text-red-600" : "font-bold text-amber-600";
  }
  return (
    <div className={className}>
      <span className="text-gray-400 text-[10px] uppercase tracking-wider">{label}:</span>{" "}
      <span className={cls}>{value}</span>
    </div>
  );
};

export default function AdminRegistrations() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [expandedUser, setExpandedUser] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [mappingUserId, setMappingUserId] = useState(null);
  const [registryInput, setRegistryInput] = useState("");
  const [mapMsg, setMapMsg] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  // Point 25 — mandatory rejection remark flow.
  const [rejectingUser, setRejectingUser] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectErr, setRejectErr] = useState("");

  useEffect(() => { loadUsers(); }, [filter, page, fromDate, toDate, showIncomplete]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter) params.append("status", filter);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (showIncomplete) params.append("include_incomplete", "true");
      const { data } = await API.get(`/admin/registrations?${params}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  const updateStatus = async (userId, status) => {
    await API.put(`/admin/registrations/${userId}/status`, { status });
    loadUsers();
  };

  const confirmReject = async () => {
    if (!rejectRemark.trim()) { setRejectErr("Please enter a rejection remark."); return; }
    setRejecting(true); setRejectErr("");
    try {
      await API.put(`/admin/registrations/${rejectingUser}/status`, { status: "rejected", remark: rejectRemark.trim() });
      setRejectingUser(null); setRejectRemark("");
      loadUsers();
    } catch (e) { setRejectErr(e.response?.data?.detail || "Failed to reject"); }
    finally { setRejecting(false); }
  };

  const mapRegistryId = async (userId) => {
    const q = (registryInput || "").trim();
    if (!q) { setMapMsg({ userId, type: "error", text: "Enter Registry ID or Serial Number" }); return; }
    setMapLoading(true); setMapMsg(null);
    try {
      const { data } = await API.put(`/admin/registrations/${userId}/registry-id`, { registry_id: q });
      setMapMsg({ userId, type: "success", text: `Mapped to Registry ${data.registry_id} — ${data.instrument_count} instrument(s) fetched from ERP.` });
      setRegistryInput("");
      setMappingUserId(null);
      await loadUsers();
    } catch (e) {
      setMapMsg({ userId, type: "error", text: e.response?.data?.detail || "Failed to map Registry ID" });
    } finally { setMapLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-registrations">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[#008A99]" />
          <div><h1 className="text-2xl font-bold text-gray-900">Registrations</h1><p className="text-sm text-gray-500">{showIncomplete ? `${total} incomplete signup${total === 1 ? "" : "s"} (started but never submitted KYC)` : `${total} total registered users`}</p></div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {["", "pending", "approved", "rejected"].map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }} disabled={showIncomplete}
            data-testid={`reg-filter-${f || "all"}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${showIncomplete ? "opacity-40 cursor-not-allowed bg-gray-100 text-gray-400" : filter === f ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {f || "All"}
          </button>
        ))}
        <button onClick={() => { setShowIncomplete(v => !v); setFilter(""); setPage(1); }}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${showIncomplete ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          data-testid="toggle-incomplete-btn"
          title="Show ONLY users who registered but never submitted KYC. These are hidden from the normal list by default. You can approve them from here.">
          {showIncomplete ? "Incomplete signups: shown" : "Show incomplete signups"}
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-500">From:</label>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }}
            className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:border-[#008A99]" data-testid="from-date-filter" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-500">To:</label>
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }}
            className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:border-[#008A99]" data-testid="to-date-filter" />
        </div>
        {(fromDate || toDate) && (
          <button onClick={() => { setFromDate(""); setToDate(""); }} className="text-xs text-red-500 hover:underline">Clear dates</button>
        )}
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.id} className="bg-white border rounded-xl overflow-hidden" data-testid="registration-item">
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{u.full_name || "—"}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[u.registration_status] || STATUS_BADGE.pending}`}>
                      {u.registration_status || "pending"}
                    </span>
                    {(!u.kyc_status || u.kyc_status === "pending") && (u.registration_status || "pending") === "pending" && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-orange-100 text-orange-700" data-testid="kyc-not-submitted-badge">
                        KYC not submitted
                      </span>
                    )}
                    {u.registry_id ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#E0F4F5] text-[#008A99]" data-testid="admin-registry-id-badge">
                        Registry: {u.registry_id}
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700" data-testid="admin-registry-missing-badge">
                        No Registry ID
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{u.mobile} | {u.email || "—"} | {u.city || "—"}, {u.state || "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  {(u.registration_status || "pending") === "pending" && (
                    <>
                      <button onClick={e => { e.stopPropagation(); updateStatus(u.id, "approved"); }}
                        className="p-2 bg-green-50 hover:bg-green-100 rounded-lg transition-colors" data-testid="approve-btn"><Check className="w-4 h-4 text-green-600" /></button>
                      <button onClick={e => { e.stopPropagation(); setRejectingUser(u.id); setRejectRemark(""); setRejectErr(""); }}
                        className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" data-testid="reject-btn"><X className="w-4 h-4 text-red-600" /></button>
                    </>
                  )}
                  {expandedUser === u.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
              {expandedUser === u.id && (
                <div className="px-4 pb-4 border-t bg-gray-50 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 text-sm">
                    <div><span className="text-gray-500 text-xs">Registry ID:</span> <span className="font-bold text-[#008A99]">{u.registry_id || "—"}</span></div>
                    <div><span className="text-gray-500 text-xs">PAN:</span> <span className="font-medium">{u.pan_number || "—"}</span></div>
                    <div><span className="text-gray-500 text-xs">KYC Status:</span> <span className="font-medium">{u.kyc_status || "—"}</span></div>
                    <div><span className="text-gray-500 text-xs">Tier:</span> <span className="ml-1 inline-block text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" style={TIER_METAL_SM[u.tier] || TIER_METAL_SM.Silver}>{u.tier || "Silver"}</span></div>
                    <div><span className="text-gray-500 text-xs">Points:</span> <span className="font-medium">{u.balance_points || 0}</span></div>
                    <div><span className="text-gray-500 text-xs">Registered:</span> <span className="font-medium">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span></div>
                    {u.rejection_remark && <div className="col-span-2 md:col-span-3"><span className="text-gray-500 text-xs">Rejection Remark:</span> <span className="font-medium text-red-600">{u.rejection_remark}</span></div>}
                  </div>
                  {(u.latitude || u.geo_city || u.geo_pincode) && (
                    <div className="bg-white rounded-lg p-3 border" data-testid="reg-location-block">
                      <p className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#008A99]" /> Captured Location</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        <AdminField label="Lat / Long" value={u.latitude ? `${Number(u.latitude).toFixed(5)}, ${Number(u.longitude).toFixed(5)}` : ""} />
                        <AdminField label="Pincode" value={u.geo_pincode} />
                        <AdminField label="Area" value={u.geo_area} />
                        <AdminField label="City" value={u.geo_city} />
                        <AdminField label="State" value={u.geo_state} />
                        {u.latitude ? <a href={`https://www.google.com/maps?q=${u.latitude},${u.longitude}`} target="_blank" rel="noopener noreferrer" className="text-[#008A99] font-semibold underline" data-testid="reg-location-map-link">View on map</a> : null}
                      </div>
                      {u.geo_display && <p className="text-[11px] text-gray-400 mt-1.5">{u.geo_display}</p>}
                    </div>
                  )}
                  {((u.instruments && u.instruments.length > 0) || (u.instrument_data && Object.keys(u.instrument_data).length > 0)) && (
                    <div className="bg-white rounded-lg p-3 border space-y-3">
                      <p className="text-xs font-semibold text-gray-500 flex items-center justify-between">
                        <span>Instrument Details (from ERP)</span>
                        {u.instruments?.length > 1 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#008A99] text-white">{u.instruments.length} INSTRUMENTS</span>}
                      </p>
                      {((u.instruments?.length > 0) ? u.instruments : [u.instrument_data]).map((inst, idx) => (
                        <div key={idx} className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs border-l-2 border-[#008A99]/30 pl-3">
                          <AdminField label="Product" value={inst.PRODUCT_SHORT_NAME} bold />
                          <AdminField label="Category" value={inst.PRODUCT_CATEGORY} />
                          <AdminField label="Sub-category" value={inst.PRODUCT_SUB_CATEGORY} />
                          <AdminField label="Serial" value={inst.SERIAL_NUMBER} />
                          <AdminField label="Item Code" value={inst.ITEM_CODE} />
                          <AdminField label="Party No." value={inst.PARTY_NUMBER} />
                          <AdminField label="Party Name" value={inst.PARTY_NAME} className="col-span-2 md:col-span-3" />
                          <AdminField label="Contract No." value={inst.CONTRACT_NUMBER} />
                          <AdminField label="Contract Type" value={inst.CONTRACT_TYPE} />
                          <AdminField label="Contract Status" value={inst.CONTRACT_STATUS} status={inst.CONTRACT_STATUS} />
                          <AdminField label="Start Date" value={adminDate(inst.CONTRACT_START_DATE)} />
                          <AdminField label="End Date" value={adminDate(inst.CONTRACT_END_DATE)} />
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Map / Update Registry ID (admin-only action) */}
                  <div className={`rounded-lg p-3 border-2 border-dashed ${u.registry_id ? "border-gray-200 bg-gray-50" : "border-amber-300 bg-amber-50"}`} data-testid="admin-registry-map-block">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Link2 className={`w-4 h-4 ${u.registry_id ? "text-gray-500" : "text-amber-600"}`} />
                        <span className="text-xs font-semibold text-gray-700">
                          {u.registry_id ? "Update Registry ID (re-fetches instruments from ERP)" : "This user registered without a Registry ID. Map one to fetch instrument details."}
                        </span>
                      </div>
                      {mappingUserId !== u.id && (
                        <button
                          onClick={() => { setMappingUserId(u.id); setRegistryInput(u.registry_id || ""); setMapMsg(null); }}
                          className={`text-xs font-bold px-3 py-1.5 rounded-lg ${u.registry_id ? "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100" : "bg-amber-500 text-white hover:bg-amber-600"}`}
                          data-testid={`admin-map-registry-btn-${u.id}`}
                        >
                          {u.registry_id ? "Update" : "Add Registry ID"}
                        </button>
                      )}
                    </div>
                    {mappingUserId === u.id && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          value={registryInput}
                          onChange={e => setRegistryInput(e.target.value)}
                          placeholder="e.g. 9621 (Registry ID) or B140811 (Serial)"
                          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#008A99]"
                          data-testid={`admin-map-registry-input-${u.id}`}
                        />
                        <button
                          onClick={() => mapRegistryId(u.id)}
                          disabled={mapLoading}
                          className="px-4 py-2 bg-[#008A99] text-white rounded-lg text-sm font-bold hover:bg-[#006670] disabled:opacity-50 flex items-center gap-1.5"
                          data-testid={`admin-map-registry-submit-${u.id}`}
                        >
                          {mapLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Fetch & Save
                        </button>
                        <button onClick={() => { setMappingUserId(null); setRegistryInput(""); setMapMsg(null); }} className="text-xs text-gray-500 hover:text-gray-700 px-2">Cancel</button>
                      </div>
                    )}
                    {mapMsg && mapMsg.userId === u.id && (
                      <div className={`mt-2 text-xs font-semibold ${mapMsg.type === "success" ? "text-green-700" : "text-red-600"}`} data-testid={`admin-map-registry-msg-${u.id}`}>
                        {mapMsg.text}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {u.pan_photo_url && <a href={u.pan_photo_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#008A99] flex items-center gap-1"><FileText className="w-3 h-3" /> PAN Photo</a>}
                    {u.gst_cert_url && <a href={u.gst_cert_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#008A99] flex items-center gap-1"><FileText className="w-3 h-3" /> GST Cert</a>}
                    {u.lab_cert_url && <a href={u.lab_cert_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#008A99] flex items-center gap-1"><FileText className="w-3 h-3" /> Lab Cert</a>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rejectingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reject-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center"><X className="w-5 h-5 text-red-600" /></div>
              <div>
                <h3 className="font-bold text-gray-900">Reject Registration</h3>
                <p className="text-xs text-gray-500">A remark is required and will be sent to the customer via SMS.</p>
              </div>
            </div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Rejection Remark *</label>
            <textarea value={rejectRemark} onChange={e => setRejectRemark(e.target.value)} rows={3} placeholder="e.g. Lab certificate is unclear — please re-upload a valid document."
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="reject-remark-input" />
            {rejectErr && <p className="text-xs text-red-600 mt-1" data-testid="reject-modal-error">{rejectErr}</p>}
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setRejectingUser(null); setRejectRemark(""); setRejectErr(""); }} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50" data-testid="reject-cancel-btn">Cancel</button>
              <button onClick={confirmReject} disabled={rejecting} className="px-5 py-2 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="reject-confirm-btn">
                {rejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Reject &amp; Notify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
